import React, {useEffect, useState} from "react";
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Text, VStack
} from "@chakra-ui/react";
import {api} from "../../../api/timesheetAPI";
import {showSwal} from "../../../common_components/Alert";
import {useAppDispatch, useAppSelector} from "../../../stateManagement/hooks";
import {jiraBoardrefreshNofilters, setTimeTableLoading, timetableRefresh} from "../../../stateManagement/refreshers";
import {Week} from "../types";
import {formatHourString, parseRawAmount} from "./hourPrecision";
import {ProgressOverlay} from "./ProgressOverlay";
import {updateBookingHours} from "./weekActionsApi";
import {isApprovedStatus, WEEKDAY_NAMES} from "./rowMapping";

interface ClearWeekDialogProps {
    isOpen: boolean;
    onClose: () => void;
    targetAnchor: string; // Monday of the week being cleared, ISO date
}

interface ClearItem {
    bookingId: string;
    status: string;
    jiraId: string;
    summary: string;
    day: number;
    hours: number;
    date: string;
}

type Phase = "loading" | "confirm" | "progress" | "idle";

export function ClearWeekDialog({isOpen, onClose, targetAnchor}: ClearWeekDialogProps) {
    const dispatch = useAppDispatch();
    const impersonate = useAppSelector((state) => state.impersonation.impersonatedUser);
    const [phase, setPhase] = useState<Phase>("idle");
    const [items, setItems] = useState<ClearItem[]>([]);
    const [totalHours, setTotalHours] = useState(0);
    const [approvedHours, setApprovedHours] = useState(0);
    const [progress, setProgress] = useState({done: 0, total: 0, message: ""});

    useEffect(() => {
        if (!isOpen) { return; }
        let cancelled = false;
        setPhase("loading");
        api.getTimeTable(targetAnchor, "weekly", impersonate).then((res) => {
            if (cancelled) { return; }
            const week: Week = res.data.weeks[0];
            const uniqueBookings = new Map<string, ClearItem>();
            let total = 0;
            let approvedTotal = 0;
            const bookings = week.bookings || {};
            const dates = week.week_dates || [];

            for (const jiraId of Object.keys(bookings)) {
                const booking = bookings[jiraId] || ({} as Week["bookings"][string]);
                const idsByDay = Array.isArray(booking.db_booking_ids) ? booking.db_booking_ids : [];
                const statusByDay = Array.isArray(booking.status) ? booking.status : [];
                const hoursByDay = Array.isArray(booking.story_booking_hours) ? booking.story_booking_hours : [];

                for (let day = 0; day < 7; day += 1) {
                    const hours = parseRawAmount(hoursByDay[day]);
                    if (hours <= 0) { continue; }
                    const status = String(statusByDay[day] || "");
                    // Approved (sent/green) bookings are never touched by Clear.
                    if (isApprovedStatus(status)) {
                        approvedTotal += hours;
                        continue;
                    }
                    total += hours;
                    const idCell = String(idsByDay[day] || "");
                    idCell.split("+").map((v) => v.trim()).filter(Boolean).forEach((bookingId) => {
                        if (!uniqueBookings.has(bookingId)) {
                            uniqueBookings.set(bookingId, {bookingId, status, jiraId, summary: booking.story_name || "", day, hours, date: dates[day]});
                        }
                    });
                }
            }

            setItems(Array.from(uniqueBookings.values()));
            setTotalHours(total);
            setApprovedHours(approvedTotal);
            if (uniqueBookings.size === 0) {
                showSwal("failed", approvedTotal > 0
                    ? `All ${formatHourString(approvedTotal)}h booked this week are already approved and cannot be cleared.`
                    : "Current week has no booked values to clear.");
                onClose();
                setPhase("idle");
                return;
            }
            setPhase("confirm");
        }).catch((err) => {
            if (cancelled) { return; }
            showSwal("failed", err?.response?.data ?? String(err));
            onClose();
            setPhase("idle");
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, targetAnchor]);

    async function handleClear() {
        setPhase("progress");
        setProgress({done: 0, total: items.length, message: "Starting..."});
        dispatch(setTimeTableLoading(true));
        const failures: string[] = [];
        try {
            for (let i = 0; i < items.length; i += 1) {
                const entry = items[i];
                const dayName = WEEKDAY_NAMES[entry.day] || `Day ${entry.day}`;
                const label = `${entry.jiraId} ${entry.summary} - ${dayName} ${formatHourString(entry.hours)}h`;
                setProgress({done: i, total: items.length, message: label});
                try {
                    await updateBookingHours({bookingId: entry.bookingId, status: entry.status, impersonate});
                } catch (err: any) {
                    console.error("WeekActions: clear failed for booking", entry.bookingId, err);
                    failures.push(`${entry.jiraId} ${dayName} \u2014 ${err?.response?.data ? String(err.response.data) : String(err)}`);
                }
                setProgress({done: i + 1, total: items.length, message: label});
            }
        } finally {
            dispatch(timetableRefresh());
            dispatch(jiraBoardrefreshNofilters());
            dispatch(setTimeTableLoading(false));
            setPhase("idle");
            onClose();
        }
        if (failures.length > 0) {
            const lines = failures.slice(0, 12).map((f) => `\u2022 ${f}`);
            showSwal("failed", `${failures.length} of ${items.length} booking(s) failed to clear and were skipped (the rest were cleared):\n\n${lines.join("\n")}`);
        }
    }

    if (phase === "progress") {
        return <ProgressOverlay isOpen title="Clearing entries..." done={progress.done} total={progress.total} message={progress.message}/>;
    }

    return (
        <Modal isOpen={isOpen && phase === "confirm"} onClose={onClose} isCentered>
            <ModalOverlay/>
            <ModalContent>
                <ModalHeader>Clear all values in current week?</ModalHeader>
                <ModalBody>
                    <VStack align="start" spacing={1}>
                        <Text>Week start: {targetAnchor}</Text>
                        <Text>Detected booked hours: {formatHourString(totalHours)}h</Text>
                        <Text>Bookings to clear: {items.length}</Text>
                        {approvedHours > 0 && (
                            <Text color="green.700">{formatHourString(approvedHours)}h already approved &mdash; will be kept, not cleared.</Text>
                        )}
                        <Text mt={2}>This will attempt to set each existing (not-yet-approved) booking to 0 hours.</Text>
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
                    <Button colorScheme="red" onClick={handleClear}>Clear</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
